import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { claimsAPI, dropdownAPI } from '../lib/api';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Loader2, 
  FileText, 
  MapPin, 
  Users, 
  Car,
  Plus,
  Trash2
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Claim Data', icon: FileText },
  { id: 2, title: 'Travel Data', icon: MapPin },
  { id: 3, title: 'People Data', icon: Users },
  { id: 4, title: 'Vehicle Data', icon: Car },
];

export default function NewClaim() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dropdowns, setDropdowns] = useState({
    districts: [],
    projectSites: [],
    vehicleTypes: [],
    vehicleOwnership: [],
    personTypes: [],
    clientSourcing: [],
  });

  const [formData, setFormData] = useState({
    // Step 1 - Claim Data
    date_of_claim: new Date().toISOString().split('T')[0],
    date_of_travel: '',
    
    // Step 2 - Travel Data
    travel_start_district: '',
    no_of_sites_planned: 1,
    project_site_location: '',
    total_expected_travel_time: '',
    total_expected_travel_distance: '',
    
    // Step 3 - People Data
    no_of_eligible_persons: 1,
    eligible_persons: [{ name: '', type: '', sourcing_person: '' }],
    
    // Step 4 - Vehicle Data
    vehicle_ownership: '',
    vehicle_type: '',
    petrol_mileage: 15,
    diesel_mileage: 18,
    cg_mileage: 20,
    ev_cost_per_km: 2.5,
  });

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchDropdowns = async () => {
    try {
      const [districts, sites, vTypes, vOwnership, pTypes, cSourcing] = await Promise.all([
        dropdownAPI.getDistricts(),
        dropdownAPI.getProjectSites(),
        dropdownAPI.getVehicleTypes(),
        dropdownAPI.getVehicleOwnership(),
        dropdownAPI.getPersonTypes(),
        dropdownAPI.getClientSourcing(),
      ]);

      setDropdowns({
        districts: districts.data,
        projectSites: sites.data,
        vehicleTypes: vTypes.data,
        vehicleOwnership: vOwnership.data,
        personTypes: pTypes.data,
        clientSourcing: cSourcing.data,
      });
    } catch (error) {
      console.error('Failed to fetch dropdowns:', error);
      toast.error('Failed to load form options');
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateEligiblePerson = (index, field, value) => {
    const newPersons = [...formData.eligible_persons];
    newPersons[index] = { ...newPersons[index], [field]: value };
    setFormData(prev => ({ ...prev, eligible_persons: newPersons }));
  };

  const addEligiblePerson = () => {
    setFormData(prev => ({
      ...prev,
      no_of_eligible_persons: prev.no_of_eligible_persons + 1,
      eligible_persons: [...prev.eligible_persons, { name: '', type: '', sourcing_person: '' }]
    }));
  };

  const removeEligiblePerson = (index) => {
    if (formData.eligible_persons.length > 1) {
      const newPersons = formData.eligible_persons.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        no_of_eligible_persons: newPersons.length,
        eligible_persons: newPersons
      }));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.date_of_claim || !formData.date_of_travel) {
          toast.error('Please fill all required fields');
          return false;
        }
        break;
      case 2:
        if (!formData.travel_start_district || !formData.project_site_location ||
            !formData.total_expected_travel_time || !formData.total_expected_travel_distance) {
          toast.error('Please fill all required fields');
          return false;
        }
        break;
      case 3:
        if (formData.eligible_persons.some(p => !p.name || !p.type)) {
          toast.error('Please fill all person details');
          return false;
        }
        break;
      case 4:
        if (!formData.vehicle_ownership || !formData.vehicle_type) {
          toast.error('Please select vehicle details');
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      const response = await claimsAPI.create(formData);
      toast.success('Claim created successfully!');
      navigate(`/claims/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create claim';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;

        return (
          <React.Fragment key={step.id}>
            <div className={`wizard-step ${isActive ? 'wizard-step-active' : ''} ${isCompleted ? 'wizard-step-completed' : 'wizard-step-pending'}`}>
              <div className="wizard-step-number">
                {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:block ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {step.title}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-8 md:w-16 h-0.5 mx-2 ${isCompleted ? 'bg-green-500' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6" data-testid="step-1-claim-data">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="date_of_claim">Date of Advance Claim *</Label>
          <Input
            id="date_of_claim"
            type="date"
            value={formData.date_of_claim}
            onChange={(e) => updateFormData('date_of_claim', e.target.value)}
            className="bg-slate-50"
            data-testid="input-date-of-claim"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="date_of_travel">Date of Proposed Travel *</Label>
          <Input
            id="date_of_travel"
            type="date"
            value={formData.date_of_travel}
            onChange={(e) => updateFormData('date_of_travel', e.target.value)}
            className="bg-slate-50"
            data-testid="input-date-of-travel"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6" data-testid="step-2-travel-data">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Travel Starting District *</Label>
          <Select
            value={formData.travel_start_district}
            onValueChange={(value) => updateFormData('travel_start_district', value)}
          >
            <SelectTrigger className="bg-slate-50" data-testid="select-district">
              <SelectValue placeholder="Select district" />
            </SelectTrigger>
            <SelectContent>
              {dropdowns.districts.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Project Site Location *</Label>
          <Select
            value={formData.project_site_location}
            onValueChange={(value) => updateFormData('project_site_location', value)}
          >
            <SelectTrigger className="bg-slate-50" data-testid="select-project-site">
              <SelectValue placeholder="Select project site" />
            </SelectTrigger>
            <SelectContent>
              {dropdowns.projectSites.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="no_of_sites">No. of Sites Planned to Visit</Label>
          <Input
            id="no_of_sites"
            type="number"
            min="1"
            value={formData.no_of_sites_planned}
            onChange={(e) => updateFormData('no_of_sites_planned', parseInt(e.target.value) || 1)}
            className="bg-slate-50"
            data-testid="input-sites-count"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="travel_time">Total Expected Travel Time (Hours) *</Label>
          <Input
            id="travel_time"
            type="number"
            step="0.5"
            min="0"
            placeholder="e.g., 4.5"
            value={formData.total_expected_travel_time}
            onChange={(e) => updateFormData('total_expected_travel_time', parseFloat(e.target.value) || 0)}
            className="bg-slate-50"
            data-testid="input-travel-time"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="travel_distance">Total Expected Travel Distance (Km) *</Label>
          <Input
            id="travel_distance"
            type="number"
            min="0"
            placeholder="e.g., 150"
            value={formData.total_expected_travel_distance}
            onChange={(e) => updateFormData('total_expected_travel_distance', parseFloat(e.target.value) || 0)}
            className="bg-slate-50"
            data-testid="input-travel-distance"
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6" data-testid="step-3-people-data">
      <div className="flex items-center justify-between">
        <Label className="text-base">Eligible Persons ({formData.eligible_persons.length})</Label>
        <Button type="button" variant="outline" size="sm" onClick={addEligiblePerson} data-testid="add-person-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Person
        </Button>
      </div>

      <div className="space-y-4">
        {formData.eligible_persons.map((person, index) => (
          <Card key={index} className="p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">Person {index + 1}</span>
              {formData.eligible_persons.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEligiblePerson(index)}
                  data-testid={`remove-person-${index}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  placeholder="Person name"
                  value={person.name}
                  onChange={(e) => updateEligiblePerson(index, 'name', e.target.value)}
                  className="bg-slate-50"
                  data-testid={`input-person-name-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={person.type}
                  onValueChange={(value) => updateEligiblePerson(index, 'type', value)}
                >
                  <SelectTrigger className="bg-slate-50" data-testid={`select-person-type-${index}`}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.personTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Client Sourcing</Label>
                <Select
                  value={person.sourcing_person}
                  onValueChange={(value) => updateEligiblePerson(index, 'sourcing_person', value)}
                >
                  <SelectTrigger className="bg-slate-50" data-testid={`select-sourcing-${index}`}>
                    <SelectValue placeholder="Select sourcing" />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdowns.clientSourcing.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6" data-testid="step-4-vehicle-data">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Vehicle Ownership *</Label>
          <Select
            value={formData.vehicle_ownership}
            onValueChange={(value) => updateFormData('vehicle_ownership', value)}
          >
            <SelectTrigger className="bg-slate-50" data-testid="select-vehicle-ownership">
              <SelectValue placeholder="Select ownership" />
            </SelectTrigger>
            <SelectContent>
              {dropdowns.vehicleOwnership.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Vehicle Type *</Label>
          <Select
            value={formData.vehicle_type}
            onValueChange={(value) => updateFormData('vehicle_type', value)}
          >
            <SelectTrigger className="bg-slate-50" data-testid="select-vehicle-type">
              <SelectValue placeholder="Select vehicle type" />
            </SelectTrigger>
            <SelectContent>
              {dropdowns.vehicleTypes.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {formData.vehicle_type && (
        <Card className="p-4 bg-slate-50">
          <Label className="text-base mb-4 block">Fuel Efficiency Details</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.vehicle_type === 'Petrol' && (
              <div className="space-y-2">
                <Label>Average Mileage (Km/Ltr)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.petrol_mileage}
                  onChange={(e) => updateFormData('petrol_mileage', parseFloat(e.target.value) || 15)}
                  className="bg-white"
                  data-testid="input-petrol-mileage"
                />
              </div>
            )}
            {formData.vehicle_type === 'Diesel' && (
              <div className="space-y-2">
                <Label>Average Mileage (Km/Ltr)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.diesel_mileage}
                  onChange={(e) => updateFormData('diesel_mileage', parseFloat(e.target.value) || 18)}
                  className="bg-white"
                  data-testid="input-diesel-mileage"
                />
              </div>
            )}
            {formData.vehicle_type === 'CNG' && (
              <div className="space-y-2">
                <Label>Average Mileage (Km/Kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.cg_mileage}
                  onChange={(e) => updateFormData('cg_mileage', parseFloat(e.target.value) || 20)}
                  className="bg-white"
                  data-testid="input-cng-mileage"
                />
              </div>
            )}
            {formData.vehicle_type === 'Electric Vehicle' && (
              <div className="space-y-2">
                <Label>Average Fuel Cost (₹/Km)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.ev_cost_per_km}
                  onChange={(e) => updateFormData('ev_cost_per_km', parseFloat(e.target.value) || 2.5)}
                  className="bg-white"
                  data-testid="input-ev-cost"
                />
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto animate-fade-in" data-testid="new-claim-form">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl">New Advance Claim</CardTitle>
            <CardDescription>Fill in the details for your travel expense claim</CardDescription>
          </CardHeader>
          <CardContent>
            <StepIndicator />

            <div className="min-h-[300px]">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                data-testid="prev-step-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>

              {currentStep < 4 ? (
                <Button type="button" onClick={nextStep} data-testid="next-step-btn">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading} data-testid="submit-claim-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Create Claim
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
